import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { getAdminDataHealth } from "../../../services/midpointApi";
import { resolveDataHealthSourceDomain } from "../../../utils/weatherDataHealthSources";
import { resolveVehicleMarketSourceDomain } from "../../../constants/vehicleMarketSourceConstants";
import DataSourceStatusTable from "./DataSourceStatusTable";
import PendingModerationCard from "./PendingModerationCard";
import RecentFailuresCard from "./RecentFailuresCard";
import RecentIngestionRunsCard from "./RecentIngestionRunsCard";
import SourceHealthBadge from "./SourceHealthBadge";
import AdminStatePanel from "./layout/AdminStatePanel";
import VehicleDataWorkflowLinks from "./vehicle/VehicleDataWorkflowLinks";

const DOMAIN_FILTER_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: "weather", label: "Weather" },
  { value: "transport", label: "Transport" },
  { value: "food", label: "Food" },
  { value: "culture", label: "Culture" },
  { value: "vehicle_market", label: "Vehicle Market" },
  { value: "real_estate", label: "Real Estate" },
  { value: "ingestion", label: "Ingestion" },
];

const HIDDEN_HEALTH_SOURCE_CODES = new Set(["IDEAM_RADAR_COMPOSITE", "COLOMBIA_LIVE_RADAR"]);

function isHiddenHealthSource(source) {
  const code = String(source?.sourceCode || source?.sourceKey || "").toUpperCase();
  const name = String(source?.sourceName || source?.displayName || "").trim().toLowerCase();
  return HIDDEN_HEALTH_SOURCE_CODES.has(code) || name === "colombia live radar";
}

function DataHealthPanelModal({ title, onClose, children }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-re-modal panel-card admin-re-modal--wide admin-data-health-panel-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="data-health-panel-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <h2 id="data-health-panel-modal-title">{title}</h2>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="admin-re-modal-body admin-data-health-panel-modal__body">{children}</div>
      </div>
    </div>,
    document.body
  );
}

function CultureAntioquiaPanel({ health }) {
  const rows = health?.cultureExpansionHealth?.municipalities || [];
  return (
    <>
      {health?.cultureExpansionHealth?.note ? (
        <p className="muted admin-dashboard__subtitle">{health.cultureExpansionHealth.note}</p>
      ) : null}
      {!rows.length ? (
        <p className="muted">No Antioquia expansion rows yet.</p>
      ) : (
        <div className="admin-data-health-dash__table-wrap">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Municipality</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Curated</th>
                <th>OSM</th>
                <th>Official source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.municipalityKey}>
                  <td>{row.displayName}</td>
                  <td>{row.tier}</td>
                  <td>{row.expansionStatus}</td>
                  <td>{row.curatedAnchorCount ?? 0}</td>
                  <td>{row.osmVenueCount ?? 0}</td>
                  <td>{row.municipalSourceStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function CultureNationalPanel({ health }) {
  const rows = health?.cultureNationalExpansionHealth?.regions || [];
  return (
    <>
      <p className="muted admin-dashboard__subtitle">
        Regional Culture expansion beyond Antioquia — starter curated coverage with honest data
        confidence labels.
      </p>
      {!rows.length ? (
        <p className="muted">No national expansion rows yet.</p>
      ) : (
        <div className="admin-data-health-dash__table-wrap">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Region</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Curated</th>
                <th>OSM</th>
                <th>Routes</th>
                <th>Festivals</th>
                <th>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.regionKey}>
                  <td>{row.displayName}</td>
                  <td>{row.priorityTier}</td>
                  <td>{row.expansionStatus}</td>
                  <td>{row.curatedAnchorCount ?? 0}</td>
                  <td>{row.osmVenueCount ?? 0}</td>
                  <td>{row.routeCount ?? 0}</td>
                  <td>{row.festivalCount ?? 0}</td>
                  <td>{row.dataConfidenceDisplay}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function CultureMunicipalEventsPanel({ health }) {
  const rows = health?.cultureMunicipalEventHealth?.sources || [];
  return (
    <>
      {health?.cultureMunicipalEventHealth?.note ? (
        <p className="muted admin-dashboard__subtitle">{health.cultureMunicipalEventHealth.note}</p>
      ) : null}
      {!rows.length ? (
        <p className="muted">No municipal event sources yet.</p>
      ) : (
        <div className="admin-data-health-dash__table-wrap">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Municipality</th>
                <th>Source key</th>
                <th>Status</th>
                <th>Enabled</th>
                <th>Live</th>
                <th>Parser</th>
                <th>Terms</th>
                <th>Last run</th>
                <th>Last success</th>
                <th>Records</th>
                <th>Health note</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.sourceKey}>
                  <td>{row.municipality}</td>
                  <td>{row.sourceKey}</td>
                  <td>{row.status}</td>
                  <td>{row.enabled ? "Yes" : "No"}</td>
                  <td>{row.isLive ? "Yes" : "No"}</td>
                  <td>{row.parserType}</td>
                  <td>{row.termsReviewStatus}</td>
                  <td>{row.lastRunAt ? new Date(row.lastRunAt).toLocaleString() : "—"}</td>
                  <td>{row.lastSuccessAt ? new Date(row.lastSuccessAt).toLocaleString() : "—"}</td>
                  <td>{row.recordsLastSeen ?? 0}</td>
                  <td>{row.healthNote || row.activationReason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

const DETAIL_PANELS = [
  {
    id: "culture-antioquia",
    label: "Culture Antioquia Expansion",
    render: (health) => <CultureAntioquiaPanel health={health} />,
  },
  {
    id: "culture-national",
    label: "Culture National Expansion",
    render: (health) => <CultureNationalPanel health={health} />,
  },
  {
    id: "culture-municipal",
    label: "Culture Municipal Event Sources",
    render: (health) => <CultureMunicipalEventsPanel health={health} />,
  },
  {
    id: "recent-runs",
    label: "Recent runs",
    render: (health) => <RecentIngestionRunsCard runs={health.recentRuns} />,
  },
  {
    id: "recent-failures",
    label: "Recent failures",
    render: (health) => (
      <RecentFailuresCard failures={health.recentFailures} runs={health.recentRuns} />
    ),
  },
  {
    id: "pending-moderation",
    label: "Pending moderation",
    render: (health, { onOpenModeration }) => (
      <PendingModerationCard
        count={health.pendingCommunityReportsCount}
        onOpenModeration={onOpenModeration}
      />
    ),
  },
];

const AdminDataHealthPage = ({
  onOpenModeration,
  onNavigateVehicleWorkflow,
  initialDomainFilter = "all",
}) => {
  const [health, setHealth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activePanelId, setActivePanelId] = useState(null);
  const [domainFilter, setDomainFilter] = useState(() =>
    resolveVehicleMarketSourceDomain(initialDomainFilter)
  );

  const loadHealth = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const data = await getAdminDataHealth();
      setHealth(data);
    } catch (error) {
      setHealth(null);
      setErrorMessage(
        error.message || "Unable to load data health. Make sure the Midpoint API is running."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const overallStatus = health?.overallStatus || "unknown";
  const activePanel = DETAIL_PANELS.find((panel) => panel.id === activePanelId) || null;

  const kpis = useMemo(() => {
    if (!health) return [];
    return [
      {
        label: "Active Sources",
        value: health.activeSources ?? 0,
        detail: `${health.totalSources ?? 0} total`,
      },
      {
        label: "Records Received",
        value: health.recordsReceived ?? 0,
        detail: `Inserted ${health.recordsInserted ?? 0}`,
      },
      {
        label: "Records Failed",
        value: health.recordsFailed ?? 0,
        tone: (health.recordsFailed ?? 0) > 0 ? "red" : null,
        detail: `${health.failedRuns ?? 0} failed runs`,
      },
      {
        label: "Pending Reports",
        value: health.pendingCommunityReportsCount ?? 0,
        tone: (health.pendingCommunityReportsCount ?? 0) > 0 ? "amber" : null,
      },
    ];
  }, [health]);

  const normalizedSources = useMemo(() => {
    return (health?.sources || [])
      .filter((source) => !isHiddenHealthSource(source))
      .map((source) => ({
        ...source,
        sourceDomain: resolveVehicleMarketSourceDomain(resolveDataHealthSourceDomain(source)),
      }));
  }, [health]);

  const domainCounts = useMemo(() => {
    return {
      all: normalizedSources.length,
      weather: normalizedSources.filter((source) => source.sourceDomain === "weather").length,
      transport: normalizedSources.filter((source) => source.sourceDomain === "transport").length,
      food: normalizedSources.filter((source) => source.sourceDomain === "food").length,
      culture: normalizedSources.filter((source) => source.sourceDomain === "culture").length,
      vehicle_market: normalizedSources.filter(
        (source) => source.sourceDomain === "vehicle_market"
      ).length,
      real_estate: normalizedSources.filter((source) => source.sourceDomain === "real_estate")
        .length,
      ingestion: normalizedSources.filter((source) => source.sourceDomain === "ingestion").length,
    };
  }, [normalizedSources]);

  const filteredSources = useMemo(() => {
    if (domainFilter === "all") {
      return normalizedSources;
    }
    return normalizedSources.filter((source) => source.sourceDomain === domainFilter);
  }, [normalizedSources, domainFilter]);

  return (
    <div className="admin-dashboard admin-data-health-dash admin-data-health-dash--full-width">
      <header className="admin-dashboard__header">
        <div className="admin-data-health-dash__title-row">
          <div>
            <h1 className="admin-dashboard__title">Data Health</h1>
            <p className="admin-dashboard__subtitle">
              Monitor source freshness, ingestion runs, and pipeline status across all domains.
              Use Vehicle Market for production ingest and compliance inventory sources.
            </p>
          </div>
          <div className="admin-data-health-dash__overall">
            <SourceHealthBadge status={overallStatus} />
          </div>
        </div>
        <div className="admin-dashboard__header-actions admin-data-health-dash__panel-nav">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadHealth}
            disabled={isLoading}
          >
            Refresh
          </button>
          {!isLoading && !errorMessage && health
            ? DETAIL_PANELS.map((panel) => (
                <button
                  key={panel.id}
                  type="button"
                  className={`admin-dashboard__action-btn${
                    activePanelId === panel.id ? " is-active" : ""
                  }`}
                  onClick={() => setActivePanelId(panel.id)}
                >
                  {panel.label}
                </button>
              ))
            : null}
        </div>
      </header>

      {isLoading ? <AdminStatePanel variant="loading" message="Loading data health…" /> : null}

      {!isLoading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={loadHealth} />
      ) : null}

      {!isLoading && !errorMessage && health ? (
        <>
          <section className="admin-dashboard__kpis">
            {kpis.map((kpi) => (
              <div className="admin-dash__kpi-card" key={kpi.label}>
                <span className="admin-dash__kpi-label">{kpi.label}</span>
                <span
                  className={`admin-dash__kpi-value${
                    kpi.tone === "green"
                      ? " admin-dash__kpi-value--green"
                      : kpi.tone === "red"
                        ? " admin-dash__kpi-value--red"
                        : kpi.tone === "amber"
                          ? " admin-dash__kpi-value--amber"
                          : ""
                  }`}
                >
                  {kpi.value}
                </span>
                {kpi.detail ? <span className="admin-dash__kpi-detail">{kpi.detail}</span> : null}
              </div>
            ))}
          </section>

          <div className="admin-dashboard__grid">
            <section className="admin-dash__card admin-dash__card--wide admin-data-health-dash__table-section">
              <div className="admin-dash__card-header">
                <h2 className="admin-dash__card-title">Data Sources</h2>
              </div>
              <div className="admin-data-health-dash__filters">
                {DOMAIN_FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={domainFilter === option.value ? "is-active" : ""}
                    onClick={() => setDomainFilter(option.value)}
                  >
                    {option.label}
                    <span className="admin-data-health-dash__filter-count">
                      {domainCounts[option.value] ?? 0}
                    </span>
                  </button>
                ))}
              </div>
              <DataSourceStatusTable sources={filteredSources} />
            </section>

            {domainFilter === "vehicle" && onNavigateVehicleWorkflow ? (
              <VehicleDataWorkflowLinks onNavigate={onNavigateVehicleWorkflow} />
            ) : null}
          </div>
        </>
      ) : null}

      {activePanel && health ? (
        <DataHealthPanelModal title={activePanel.label} onClose={() => setActivePanelId(null)}>
          {activePanel.render(health, { onOpenModeration })}
        </DataHealthPanelModal>
      ) : null}
    </div>
  );
};

export default AdminDataHealthPage;
